import cx from 'classnames'
import { useCombobox } from 'downshift'
import { useState } from 'react'

import { AutocompleteOption } from '@lib/dom'

import { InputFieldWithManagedStateProps } from './input-field/with-managed-state'

type InputWithAutocompleteProps = Pick<
  InputFieldWithManagedStateProps,
  | 'type'
  | 'label'
  | 'isRequired'
  | 'placeholder'
  | 'maxLength'
  | 'errorMessage'
  | 'borderBottom'
  | 'isInvertedColors'
  | 'hasCustomInputStyles'
  | 'className'
  | 'labelClassName'
  | 'inputWrapperClassName'
  | 'inputClassName'
  | 'children'
> & {
  autocompleteOptions: AutocompleteOption[]
  defaultValue?: string
  onChange: (newValue: string) => void
}

const InputWithAutocomplete = ({
  autocompleteOptions,
  defaultValue = '',
  type,
  label,
  isRequired,
  placeholder,
  maxLength,
  errorMessage,
  borderBottom,
  isInvertedColors,
  hasCustomInputStyles,
  onChange,
  className,
  labelClassName,
  inputWrapperClassName,
  inputClassName,
  children,
}: InputWithAutocompleteProps) => {
  const [items, setItems] = useState<AutocompleteOption[]>(autocompleteOptions)

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items,
    defaultInputValue: defaultValue,
    itemToString: (item) => item?.title ?? '',
    onInputValueChange: (changes) => {
      onChange(changes.inputValue)

      // Filter autocomplete items
      const lowerCasedInputValue = changes.inputValue.toLowerCase()
      const newAutocompleteItems = autocompleteOptions.filter(
        (option: AutocompleteOption) => {
          return (
            !changes.inputValue ||
            option.title.toLowerCase().includes(lowerCasedInputValue)
          )
        },
      )

      setItems(newAutocompleteItems)
    },
  })

  const labelProps = getLabelProps()
  const menuProps = getMenuProps()
  const inputProps = getInputProps()

  return (
    <div className={className}>
      <div className="grid">
        <div className="flex flex-col relative text-left">
          {!!label && (
            <label
              className={cx(
                'text-sm',
                {
                  'mb-1': !hasCustomInputStyles,
                },
                labelClassName,
              )}
              {...labelProps}
            >
              {label}
              {isRequired && <span className="ml-0.5 text-red">*</span>}
            </label>
          )}

          <div
            className={cx(
              'flex justify-between',
              {
                'border-error': !hasCustomInputStyles && errorMessage,
                'border-input-border':
                  !hasCustomInputStyles && !errorMessage && !isInvertedColors,
                'border-input-inverted-border':
                  !hasCustomInputStyles && !errorMessage && isInvertedColors,
                'border-b': !hasCustomInputStyles && borderBottom,
                'border px-4': !hasCustomInputStyles && !borderBottom,
              },
              inputWrapperClassName,
            )}
          >
            <input
              type={type}
              inputMode="text"
              maxLength={maxLength}
              placeholder={placeholder}
              className={cx(
                'relative appearance-none w-full h-full',
                {
                  'py-2 text-base leading-none': !hasCustomInputStyles,
                  'bg-input-bg text-input-text placeholder-input-placeholder':
                    !hasCustomInputStyles && !isInvertedColors,
                  'bg-input-inverted-bg text-input-inverted-text placeholder-input-inverted-placeholder':
                    !hasCustomInputStyles && isInvertedColors,
                },
                inputClassName,
              )}
              {...inputProps}
            />
            {children}
          </div>

          {!!errorMessage && (
            <span role="alert" className="mt-2 text-xs text-error">
              {errorMessage}
            </span>
          )}
        </div>
      </div>

      <ul
        className={cx(
          'absolute w-96 max-h-80 p-0 mt-1 shadow-md overflow-scroll bg-white z-10',
          {
            hidden: !isOpen || items.length === 0,
          },
        )}
        {...menuProps}
      >
        {isOpen && (
          <>
            {items.map((item, index) => {
              const itemProps = getItemProps({
                item,
                index,
              })

              return (
                <li
                  key={item.value}
                  className={cx(
                    'flex flex-col py-2 px-3 shadow-sm cursor-pointer',
                    highlightedIndex === index && 'bg-gray-lightest',
                  )}
                  {...itemProps}
                >
                  <span>{item.title}</span>
                </li>
              )
            })}
          </>
        )}
      </ul>
    </div>
  )
}

export default InputWithAutocomplete
